import React from 'react';

import { Hsla, hslaToString } from 'dashboards/services/color';
import { InferenceDetection } from 'files/hooks/useImageInference';

import { cropKey } from './useDetectionCrops';

export type DetectionKind = 'person' | 'face';

export type LabeledDetection = {
  kind: DetectionKind;
  detection: InferenceDetection;
  color: Hsla;
};

type DetectionOverlayProps = {
  imageUrl: string;
  detections: LabeledDetection[];
  isLoading?: boolean;
  cropsReady?: boolean;
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  onSelect: (labeled: LabeledDetection) => void;
};

export function DetectionOverlay({
  imageUrl,
  detections,
  isLoading,
  cropsReady = true,
  hoveredKey,
  onHoverChange,
  onSelect,
}: DetectionOverlayProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = React.useState<{
    naturalWidth: number;
    naturalHeight: number;
    displayWidth: number;
    displayHeight: number;
  } | null>(null);

  function handleImageLoad(event: React.SyntheticEvent<HTMLImageElement>) {
    const img = event.currentTarget;
    setImageSize({
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      displayWidth: img.clientWidth,
      displayHeight: img.clientHeight,
    });
  }

  function getBboxStyle(bbox: [number, number, number, number]) {
    if (!imageSize) return { display: 'none' as const };

    const [x1, y1, x2, y2] = bbox;
    const scaleX = imageSize.displayWidth / imageSize.naturalWidth;
    const scaleY = imageSize.displayHeight / imageSize.naturalHeight;

    return {
      left: x1 * scaleX,
      top: y1 * scaleY,
      width: (x2 - x1) * scaleX,
      height: (y2 - y1) * scaleY,
    };
  }

  return (
    <div
      ref={containerRef}
      className="relative inline-block max-h-[50vh] max-w-full overflow-hidden rounded-md"
    >
      <img
        src={imageUrl}
        alt="Detections"
        className="block h-auto max-h-[50vh] w-auto max-w-full"
        style={{ opacity: isLoading ? 0.5 : 1 }}
        onLoad={handleImageLoad}
      />

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="gap-sm flex flex-col items-center">
            <div
              className="rounded-full border-2 border-white/30"
              style={{
                width: 24,
                height: 24,
                borderTopColor: 'white',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span className="text-sm text-white">Detecting...</span>
          </div>
        </div>
      )}

      {imageSize &&
        detections.map(({ detection, color, kind }) => {
          const style = getBboxStyle(detection.bbox);
          const key = cropKey(kind, detection.detection_id);
          const isHovered = hoveredKey === key;

          return (
            <button
              key={key}
              type="button"
              disabled={!cropsReady}
              className="absolute border-2 border-solid transition-all"
              style={{
                ...style,
                borderColor: hslaToString(color, 1),
                backgroundColor: hslaToString(color, isHovered ? 0.3 : 0.08),
                cursor: cropsReady ? 'pointer' : 'wait',
              }}
              onMouseEnter={cropsReady ? () => onHoverChange(key) : undefined}
              onMouseLeave={cropsReady ? () => onHoverChange(null) : undefined}
              onClick={() => onSelect({ kind, detection, color })}
              title={
                cropsReady
                  ? `${kind === 'person' ? 'Person' : 'Face'} (${Math.round(detection.score * 100)}% confidence)`
                  : 'Loading...'
              }
            >
              <span
                className="absolute bottom-0 left-0 px-1 text-xs text-white"
                style={{ backgroundColor: hslaToString(color, 1) }}
              >
                {Math.round(detection.score * 100)}%
              </span>
            </button>
          );
        })}
    </div>
  );
}
